import { useEffect, useRef } from "react";

import { shouldRefetch } from "./refresh";

/**
 * Run `read` once on mount, then again whenever the tab comes back to the foreground.
 *
 * **No interval.** The read path's whole design is that nothing syncs while you sit reading, and a
 * counter is not worth breaking that for. Coming back to the tab is the one moment the number could
 * have moved without this app knowing — you studied in Anki desktop, or the phone synced.
 *
 * `read` is called with no arguments and its result ignored; it owns its own error handling, since
 * every caller here would rather keep the last known number than replace it with a zero.
 *
 * **`key` is what the read is *about*** — pass it when the answer depends on something that can
 * change while the component stays mounted (Review's landing depends on `deck`). A key change
 * re-reads immediately and **bypasses `shouldRefetch`**, because it is a different question rather
 * than a repeat of the same one; the 5-second window exists only to collapse the `focus` /
 * `visibilitychange` pair a single tab switch fires, and applying it to a key change would drop the
 * first read of a screen somebody had only just left.
 *
 * **Taking the key here rather than leaving callers to write a `[key]` effect beside the hook is
 * the whole point.** Both would fire at mount, and the duplicate is a real cost rather than an
 * untidiness: for the Review landing it is a second `/api/review/decks` on every visit —
 * `deck_due_tree()` plus a `find_cards` per deck, `read_fast` on the single interactive worker
 * thread, in front of everything else the browser is waiting on.
 *
 * Omitting it is the `useDeckCounts` case — one global question, no key, and `[undefined]` is a
 * stable dep, so nothing about that caller changes.
 */
export function useRefreshOnFocus(read: () => void, key?: unknown): void {
  const lastAt = useRef(0);
  // The effect must not re-run when the caller passes a fresh closure, or every render would
  // re-register the listeners; the ref keeps the latest one reachable without that.
  const latest = useRef(read);
  latest.current = read;

  useEffect(() => {
    const run = () => {
      const now = Date.now();
      if (!shouldRefetch(lastAt.current, now)) return;
      lastAt.current = now;
      latest.current();
    };

    // Mount, and every key change after it. Unconditional — see the docstring — but it still
    // stamps the window, so a `focus` arriving a moment later is correctly treated as the same
    // question and does not read twice.
    lastAt.current = Date.now();
    latest.current();

    const onVisible = () => {
      if (document.visibilityState === "visible") run();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", run);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", run);
    };
  }, [key]);
}
